/**
 * Storage Service
 *
 * Abstraction layer for localStorage operations.
 * Provides a single point of control for all storage operations,
 * making it easy to switch storage strategies or add encryption.
 *
 * Usage:
 *   import { storageService } from '@/services/storageService';
 *   storageService.setToken('...');
 *   const token = storageService.getToken();
 */

import { AUTH_TOKEN_KEY, AUTH_USER_KEY } from '../config/environment';

class StorageService {
  // Generic methods
  get(key) {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      // Try to parse as JSON, return raw string if parsing fails
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    } catch (error) {
      console.error(`StorageService.get error for key "${key}":`, error);
      return null;
    }
  }

  set(key, value) {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`StorageService.set error for key "${key}":`, error);
      return false;
    }
  }

  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`StorageService.remove error for key "${key}":`, error);
      return false;
    }
  }

  clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('StorageService.clear error:', error);
      return false;
    }
  }

  // Auth-specific methods (convenience wrappers)
  getToken() {
    return this.get(AUTH_TOKEN_KEY);
  }

  setToken(token) {
    return this.set(AUTH_TOKEN_KEY, token);
  }

  removeToken() {
    return this.remove(AUTH_TOKEN_KEY);
  }

  getUser() {
    return this.get(AUTH_USER_KEY);
  }

  setUser(user) {
    return this.set(AUTH_USER_KEY, user);
  }

  removeUser() {
    return this.remove(AUTH_USER_KEY);
  }

  // Clear all auth data
  clearAuth() {
    this.removeToken();
    this.removeUser();
  }

  // Check if user is authenticated (has token)
  isAuthenticated() {
    return !!this.getToken();
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Also export class for testing purposes
export { StorageService };
